/**
 * utils/UsageTrackingService.js
 * Service pour tracker l'utilisation de l'application SMART-PM
 */

import { smartPmBackend } from "./SmartPmBackendService.js";
import logger from "./logger.js";

class UsageTrackingService {
  /**
   * Extraire les informations détaillées du navigateur
   * @returns {Object} Informations complètes sur le navigateur et l'environnement
   */
  getBrowserInfo() {
    const ua = navigator.userAgent;
    let browserName = "Unknown";
    let browserVersion = "Unknown";
    let osName = "Unknown";

    // Détection du navigateur
    if (ua.indexOf("Firefox") > -1) {
      browserName = "Firefox";
      browserVersion = ua.match(/Firefox\/([0-9.]+)/)?.[1] || "Unknown";
    } else if (ua.indexOf("Chrome") > -1 && ua.indexOf("Edg") === -1) {
      browserName = "Chrome";
      browserVersion = ua.match(/Chrome\/([0-9.]+)/)?.[1] || "Unknown";
    } else if (ua.indexOf("Edg") > -1) {
      browserName = "Edge";
      browserVersion = ua.match(/Edg\/([0-9.]+)/)?.[1] || "Unknown";
    } else if (ua.indexOf("Safari") > -1) {
      browserName = "Safari";
      browserVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || "Unknown";
    }

    // Détection de l'OS
    if (ua.indexOf("Win") > -1) osName = "Windows";
    else if (ua.indexOf("Mac") > -1) osName = "macOS";
    else if (ua.indexOf("Linux") > -1) osName = "Linux";
    else if (ua.indexOf("Android") > -1) osName = "Android";
    else if (ua.indexOf("iOS") > -1) osName = "iOS";

    // Informations d'écran
    const screenInfo = {
      width: screen.width,
      height: screen.height,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio || 1,
    };

    // Informations de viewport
    const viewportInfo = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Informations système
    const systemInfo = {
      language: navigator.language,
      languages: navigator.languages || [navigator.language],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      touchSupport: navigator.maxTouchPoints > 0,
      cookiesEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine,
    };

    // Informations optionnelles (peuvent ne pas être disponibles)
    const optionalInfo = {};

    // Connexion réseau (Chrome/Edge)
    if (navigator.connection) {
      optionalInfo.connection = {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData,
      };
    }

    // Mémoire (Chrome uniquement)
    if (navigator.deviceMemory) {
      optionalInfo.deviceMemory = `${navigator.deviceMemory} GB`;
    }

    // Nombre de processeurs logiques
    if (navigator.hardwareConcurrency) {
      optionalInfo.cpuCores = navigator.hardwareConcurrency;
    }

    // Orientation écran
    if (screen.orientation) {
      optionalInfo.screenOrientation = screen.orientation.type;
    }

    return {
      browser: browserName,
      browser_version: browserVersion,
      os: osName,
      user_agent: ua,
      screen: screenInfo,
      viewport: viewportInfo,
      system: systemInfo,
      optional: optionalInfo,
    };
  }

  /**
   * Extraire les informations détaillées des fichiers uploadés
   * @param {FileList|Array<Object>} files - Liste des fichiers (FileList ou données File System Access)
   * @returns {Object} Informations structurées sur les fichiers
   */
  extractFilesInfo(files) {
    // Détecter File System Access API (nouveau format)
    if (Array.isArray(files) && files[0] && files[0].kind === "directory" && files[0].items) {
      const data = files[0];
      return {
        parent_folder: data.name,
        total_files: data.totalFiles || 0,
        total_size_bytes: data.totalSize || 0,
        total_size_mb: ((data.totalSize || 0) / (1024 * 1024)).toFixed(2),
        items: data.items || [],
        all_files: [], // Pas disponible avec File System Access
        api_type: "file_system_access",
      };
    }

    // Format FileList (ancienne API)
    const filesArray = Array.from(files);
    let totalSize = 0;
    const pathParts = filesArray[0]?.webkitRelativePath?.split("/") || [];
    const parentFolder = pathParts[0] || "Unknown";

    const firstLevelItems = new Map();
    const allFilesDetailed = [];

    filesArray.forEach((file) => {
      const parts = file.webkitRelativePath.split("/");
      if (parts.length >= 2) {
        const firstLevel = parts[1];
        const relativePath = parts.slice(1).join("/");

        if (!firstLevelItems.has(firstLevel)) {
          firstLevelItems.set(firstLevel, {
            name: firstLevel,
            type: parts.length === 2 ? "file" : "folder",
            size: 0,
            filesCount: 0,
            files: [],
          });
        }

        const item = firstLevelItems.get(firstLevel);
        item.size += file.size;
        item.filesCount += 1;
        totalSize += file.size;

        item.files.push({
          name: file.name,
          path: relativePath,
          size: file.size,
          size_kb: (file.size / 1024).toFixed(2),
          type: file.type || "unknown",
        });

        allFilesDetailed.push({
          name: file.name,
          full_path: file.webkitRelativePath,
          relative_path: relativePath,
          size: file.size,
          size_kb: (file.size / 1024).toFixed(2),
          type: file.type || "unknown",
          extension: file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "none",
        });
      }
    });

    return {
      parent_folder: parentFolder,
      total_files: filesArray.length,
      total_size_bytes: totalSize,
      total_size_mb: (totalSize / (1024 * 1024)).toFixed(2),
      items: Array.from(firstLevelItems.values()),
      all_files: allFilesDetailed,
      api_type: "legacy_file_input",
    };
  }

  /**
   * Tracker l'upload d'un dossier (non-bloquant)
   * @param {FileList|FileSystemAccessTrackingData[]} files - Liste des fichiers (FileList ou données File System Access)
   * @returns {Promise<Object>} Résultat du tracking
   */
  async trackUpload(files) {
    try {
      const browserInfo = this.getBrowserInfo();
      const filesInfo = this.extractFilesInfo(files);

      const trackingData = {
        action: "upload_directory",
        timestamp_client: new Date().toISOString(),
        browser_info: browserInfo,
        upload_info: filesInfo,
      };

      logger.info(
        {
          context: "usage-tracking-request",
          parentFolder: filesInfo.parent_folder,
          totalFiles: filesInfo.total_files,
          totalSizeMb: filesInfo.total_size_mb,
          browser: browserInfo.browser,
          os: browserInfo.os,
        },
        "Envoi tracking upload"
      );

      const { success, response } = await smartPmBackend.request("/track-usage", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(trackingData),
      });

      if (!success || !response.ok) {
        logger.warn(
          {
            context: "track-upload-failed",
            status: response?.status,
            statusText: response?.statusText,
          },
          "Erreur tracking (non-bloquant)"
        );
        return { success: false, error: response?.statusText || "Unknown error" };
      }

      const result = await response.json();
      logger.info(
        {
          context: "track-upload-success",
          parentFolder: filesInfo.parent_folder,
          totalFiles: filesInfo.total_files,
        },
        "Upload tracké avec succès"
      );
      return { success: true, ...result };
    } catch (error) {
      // Le tracking ne doit jamais faire échouer l'upload principal
      logger.warn(
        {
          context: "usage-tracking-error",
          error: error.message,
          stack: error.stack,
        },
        "Erreur UsageTracking (non-bloquant)"
      );
      return { success: false, error: error.message };
    }
  }
}

export const usageTrackingService = new UsageTrackingService();
